import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { reportAPI } from '../../api/reports';
import { Download, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import { DateRangeQuickFilter } from '../../components/UI/DateRangeQuickFilter';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const formatCurrency = (value: number) => new Intl.NumberFormat('vi-VN').format(value);
const formatCurrencyShort = (value: number) => {
  if (value >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(1)}B`;
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(0)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(0)}K`;
  return String(value);
};

interface VehicleRow {
  vehicleId: number;
  licensePlate: string;
  tripCount: number;
  tripExpenses: number;
  vehicleExpenses: number;
  totalExpenses: number;
  totalDriverWages: number;
  totalCost: number;
}

interface ReportData {
  vehicles: VehicleRow[];
  summary: {
    totalExpenses: number;
    totalDriverWages: number;
    totalCost: number;
    totalTrips: number;
  };
}

type ChartMode = 'tripCount' | 'totalCost';

export const VehicleProfitability: React.FC = () => {
  const { t } = useTranslation();
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [data, setData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [chartMode, setChartMode] = useState<ChartMode>('tripCount');

  const handleGenerate = async () => {
    if (!startDate || !endDate) {
      toast.error(t('reports.selectDateRange'));
      return;
    }
    setLoading(true);
    try {
      const result = await reportAPI.vehicleProfitability({ startDate, endDate });
      setData(result as any);
    } catch (error) {
      toast.error(t('reports.generateError'));
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (!startDate || !endDate) return;
    try {
      const blob = await reportAPI.exportExcel({ type: 'vehicle-profitability', startDate, endDate });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report-vehicle-${startDate}-${endDate}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(t('reports.exportSuccess'));
    } catch {
      toast.error(t('reports.exportError'));
    }
  };

  // Chart data
  const chartData = data?.vehicles.map((v) => ({
    name: v.licensePlate,
    [t('reports.tripCount')]: v.tripCount,
    [t('reports.totalExpenses')]: v.totalExpenses,
    [t('salaries.driverWagePerTrip')]: v.totalDriverWages,
    totalCost: v.totalCost,
  })) ?? [];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">{t('reports.vehicleProfitability')}</h1>

      {/* Filters */}
      <div className="card">
        <div className="flex items-end space-x-4">
          <DateRangeQuickFilter
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
          />
          <button onClick={handleGenerate} disabled={loading} className="btn-primary flex items-center">
            <Search className="w-4 h-4 mr-2" />
            {loading ? t('common.loading') : t('reports.generate')}
          </button>
          {data && data.vehicles.length > 0 && (
            <button onClick={handleExport} className="btn-secondary flex items-center">
              <Download className="w-4 h-4 mr-2" />
              {t('reports.exportExcel')}
            </button>
          )}
        </div>
      </div>

      {data && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="card text-center">
              <p className="text-sm text-gray-500">{t('reports.tripCount')}</p>
              <p className="text-2xl font-bold text-blue-600">{data.summary.totalTrips}</p>
            </div>
            <div className="card text-center">
              <p className="text-sm text-gray-500">{t('reports.totalExpenses')}</p>
              <p className="text-2xl font-bold text-orange-600">{formatCurrency(data.summary.totalExpenses)}</p>
            </div>
            <div className="card text-center">
              <p className="text-sm text-gray-500">{t('trips.driverWage')}</p>
              <p className="text-2xl font-bold text-purple-600">{formatCurrency(data.summary.totalDriverWages)}</p>
            </div>
            <div className="card text-center">
              <p className="text-sm text-gray-500">Tổng chi phí</p>
              <p className="text-2xl font-bold text-red-600">{formatCurrency(data.summary.totalCost)}</p>
            </div>
          </div>

          {/* Chart */}
          {data.vehicles.length > 0 && (
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">Hiệu suất theo xe</h2>
                <div className="flex rounded-lg border overflow-hidden text-sm">
                  <button
                    onClick={() => setChartMode('tripCount')}
                    className={`px-3 py-1.5 ${chartMode === 'tripCount' ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                  >
                    Số chuyến
                  </button>
                  <button
                    onClick={() => setChartMode('totalCost')}
                    className={`px-3 py-1.5 ${chartMode === 'totalCost' ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                  >
                    Tổng chi phí
                  </button>
                </div>
              </div>

              <ResponsiveContainer width="100%" height={280}>
                {chartMode === 'tripCount' ? (
                  <BarChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip formatter={(v: number) => [v, 'Số chuyến']} />
                    <Bar dataKey={t('reports.tripCount')} fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                ) : (
                  <BarChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis tickFormatter={formatCurrencyShort} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v: number) => [`${formatCurrency(v)} đ`]} />
                    <Legend />
                    <Bar dataKey={t('reports.totalExpenses')} fill="#f97316" stackId="a" radius={[0, 0, 0, 0]} />
                    <Bar dataKey={t('salaries.driverWagePerTrip')} fill="#a855f7" stackId="a" radius={[4, 4, 0, 0]} />
                  </BarChart>
                )}
              </ResponsiveContainer>
            </div>
          )}

          {/* Table */}
          {data.vehicles.length > 0 && (
            <div className="card overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b">
                  <tr className="text-left">
                    <th className="pb-3 table-header">{t('vehicles.licensePlate')}</th>
                    <th className="pb-3 table-header text-right">{t('reports.tripCount')}</th>
                    <th className="pb-3 table-header text-right">Chi phí chuyến</th>
                    <th className="pb-3 table-header text-right">Chi phí xe</th>
                    <th className="pb-3 table-header text-right">{t('trips.driverWage')}</th>
                    <th className="pb-3 table-header text-right">Tổng chi phí</th>
                  </tr>
                </thead>
                <tbody>
                  {data.vehicles.map((row) => (
                    <tr key={row.vehicleId} className="border-b last:border-0">
                      <td className="py-3 font-medium">{row.licensePlate}</td>
                      <td className="py-3 text-right">{row.tripCount}</td>
                      <td className="py-3 text-right">{formatCurrency(row.tripExpenses)}</td>
                      <td className="py-3 text-right">{formatCurrency(row.vehicleExpenses)}</td>
                      <td className="py-3 text-right text-purple-600">{formatCurrency(row.totalDriverWages)}</td>
                      <td className="py-3 text-right font-semibold text-red-600">{formatCurrency(row.totalCost)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="border-t bg-gray-50">
                  <tr>
                    <td className="py-3 font-semibold">Tổng</td>
                    <td className="py-3 text-right font-semibold">{data.summary.totalTrips}</td>
                    <td className="py-3 text-right font-semibold">
                      {formatCurrency(data.vehicles.reduce((s, r) => s + r.tripExpenses, 0))}
                    </td>
                    <td className="py-3 text-right font-semibold">
                      {formatCurrency(data.vehicles.reduce((s, r) => s + r.vehicleExpenses, 0))}
                    </td>
                    <td className="py-3 text-right font-semibold text-purple-600">
                      {formatCurrency(data.summary.totalDriverWages)}
                    </td>
                    <td className="py-3 text-right font-semibold text-red-600">
                      {formatCurrency(data.summary.totalCost)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {data.vehicles.length === 0 && (
            <div className="card text-center text-gray-500 py-10">{t('common.noData')}</div>
          )}
        </>
      )}
    </div>
  );
};
